import { TreePine } from 'lucide-react';
import { useAnimate } from 'motion/react-mini';
import { type ReactNode, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { GameArrivalContext, type GameArrivalPhase } from '../hooks/use-game-arrival';

function subscribeReducedMotion(notify: () => void) {
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  media.addEventListener('change', notify);
  return () => media.removeEventListener('change', notify);
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function GameArrival({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<GameArrivalPhase>('loading');
  const [ornament, setOrnament] = useState(false);
  const [longWait, setLongWait] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [finished, setFinished] = useState(false);
  const ornamentShownAt = useRef<number | null>(null);
  const reducedMotion = useSyncExternalStore(subscribeReducedMotion, prefersReducedMotion, () => true);
  const [scope, animate] = useAnimate<HTMLDivElement>();

  useLayoutEffect(() => {
    if (phase !== 'loading') return;
    setOrnament(false);
    setLongWait(false);
    setRevealing(false);
    setFinished(false);
    ornamentShownAt.current = null;
    if (scope.current) scope.current.style.opacity = '1';
    const ornamentTimer = window.setTimeout(() => setOrnament(true), 200);
    const guidanceTimer = window.setTimeout(() => setLongWait(true), 8000);
    return () => {
      window.clearTimeout(ornamentTimer);
      window.clearTimeout(guidanceTimer);
    };
  }, [phase, scope]);

  useLayoutEffect(() => {
    ornamentShownAt.current = ornament ? performance.now() : null;
  }, [ornament]);

  useLayoutEffect(() => {
    if (phase !== 'ready' || finished) return;
    let active = true;
    let animation: ReturnType<typeof animate> | undefined;
    const remaining =
      ornamentShownAt.current === null ? 0 : Math.max(0, 500 - (performance.now() - ornamentShownAt.current));
    let frame = 0;
    const revealTimer = window.setTimeout(() => {
      frame = requestAnimationFrame(() => {
        // Let the renderer paint the wired per-seat view before uncovering it.
        frame = requestAnimationFrame(() => {
          if (reducedMotion) {
            setFinished(true);
            return;
          }
          setRevealing(true);
          animation = animate(scope.current, { opacity: [1, 0] }, { duration: 0.32, ease: 'easeOut' });
          void animation.then(() => {
            if (active) setFinished(true);
          });
        });
      });
    }, remaining);
    return () => {
      active = false;
      window.clearTimeout(revealTimer);
      cancelAnimationFrame(frame);
      animation?.cancel();
    };
  }, [phase, finished, ornament, reducedMotion, animate, scope]);

  const covered = phase !== 'error' && !finished;
  return (
    <GameArrivalContext value={setPhase}>
      <div
        className="game-arrival-host"
        data-game-arrival
        data-reduced-motion={reducedMotion}
        data-phase={phase === 'error' ? 'error' : finished ? 'complete' : revealing ? 'revealing' : 'loading'}
      >
        {children}
        {covered && (
          <div
            ref={scope}
            className="game-arrival"
            data-arrival-cover
            data-revealing={revealing}
            data-long-wait={longWait}
          >
            {phase === 'loading' && (
              <p role="status" className="sr-only">
                {longWait
                  ? 'Still preparing your table. Waiting for game resources or a connection.'
                  : 'Preparing your table'}
              </p>
            )}
            <div className="game-arrival-content" data-arrival-content hidden={!ornament} aria-hidden="true">
              <div className="game-arrival-cards">
                {[-1, 0, 1].map((position) => (
                  <div className="game-arrival-card-position" data-position={position} key={position}>
                    <div className="game-arrival-card">
                      <span className="game-arrival-card-inlay">
                        <TreePine strokeWidth={1.25} />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="game-arrival-brand">CardForest</p>
              <p className="game-arrival-title">{longWait ? 'Still preparing your table' : 'Preparing your table'}</p>
              <p className={longWait ? 'game-arrival-guidance' : 'game-arrival-caption'}>
                {longWait ? 'Waiting for game resources or a connection.' : 'A little shuffle. A fresh start.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </GameArrivalContext>
  );
}
